import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { GenericChart } from "./GenericChart";
import type { EntryViewT } from "@patient-watch/types/database";
import { stateIcons } from "@patient-watch/icons";

interface DiaryState {
    patient_name: string;
    current_state: "PAIN_REDUCED" | "PAIN_SPIKE" | "NEVER_WORKED";
    entries: EntryViewT[];
}

// Helper to create entry going back from today
const createEntry = (id: string, weeksAgo: number, label: string, value: number): EntryViewT =>
    ({
        entry_id: id,
        patient_id: "demo-patient",
        calculated_entry_time: dayjs().subtract(weeksAgo, "weeks").toISOString(),
        timing_label: label,
        entry_values: [
            {
                input_id: "pain-score",
                form_id: "pain-form",
                input_key: "number",
                input_label: "Pain Score",
                chart_order: 1,
                max_score: 10,
                is_primary_score: true,
                number_value: value,
                text_value: String(value),
            },
        ],
    }) as unknown as EntryViewT;

const exampleDiaries: DiaryState[] = [
    {
        patient_name: "Nina Simone",
        current_state: "PAIN_REDUCED",
        entries: [
            createEntry("1", 16, "Pre", 9),
            createEntry("2", 16, "Post", 9),
            createEntry("3", 16, "4h", 9),
            createEntry("4", 16, "24h", 8),
            createEntry("5", 16, "48h", 5),
            createEntry("6", 15, "1 week", 2),
            createEntry("7", 14, "2 weeks", 2),
            createEntry("8", 12, "4 weeks", 5),
            createEntry("9", 10, "6 weeks", 3),
            createEntry("10", 8, "8 weeks", 2),
            createEntry("11", 6, "10 weeks", 3),
            createEntry("12", 4, "12 weeks", 2),
            createEntry("13", 2, "14 weeks", 2),
            createEntry("14", 0, "16 weeks", 2),
        ],
    },
    {
        patient_name: "Otis Redding",
        current_state: "PAIN_SPIKE",
        entries: [
            createEntry("1", 9, "Pre", 8),
            createEntry("2", 9, "Post", 9),
            createEntry("3", 9, "4h", 9),
            createEntry("4", 9, "24h", 4),
            createEntry("5", 9, "48h", 3),
            createEntry("6", 8, "1 week", 2),
            createEntry("7", 7, "2 weeks", 4),
            createEntry("8", 5, "4 weeks", 7),
            createEntry("9", 3, "6 weeks", 8),
        ],
    },
    {
        patient_name: "Aretha Franklin",
        current_state: "NEVER_WORKED",
        entries: [
            createEntry("1", 5, "Pre", 8),
            createEntry("2", 5, "Post", 9),
            createEntry("3", 5, "4h", 9),
            createEntry("4", 5, "24h", 8),
            createEntry("5", 5, "48h", 8),
        ],
    },
];

const { Flag } = stateIcons;

interface TabProps {
    active: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    words: string;
    color: "green" | "amber" | "red";
}

const Tab = ({ active, onClick, icon, words, color }: TabProps) => {
    const hoverColors = {
        green: "hover:bg-green-50 dark:hover:bg-green-950/40",
        amber: "hover:bg-amber-50 dark:hover:bg-amber-950/40",
        red: "hover:bg-red-50 dark:hover:bg-red-950/40",
    };

    const activeColors = {
        green: "bg-green-100 text-green-800 border border-green-300 dark:bg-green-950/50 dark:text-green-300 dark:border-green-800",
        amber: "bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800",
        red: "bg-red-100 text-red-800 border border-red-300 dark:bg-red-950/50 dark:text-red-300 dark:border-red-800",
    };

    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                active ? activeColors[color] : `bg-transparent text-slate-600 dark:text-slate-400 ${hoverColors[color]}`
            }`}
        >
            {icon}
            <span className="font-medium text-sm">{words}</span>
        </button>
    );
};

export function DiaryDemoChart() {
    const [diaryIndex, setDiaryIndex] = useState<number>(0);
    const [isPaused, setIsPaused] = useState(false);
    const autoAdvanceRef = useRef<number | null>(null);
    const currentDiary = exampleDiaries[diaryIndex];

    const getStateStyles = (state: string) => {
        switch (state) {
            case "PAIN_REDUCED":
                return "bg-green-100 border-green-300 text-green-700 dark:bg-green-950/50 dark:border-green-800 dark:text-green-300";
            case "PAIN_SPIKE":
                return "bg-amber-100 border-amber-300 text-amber-700 dark:bg-amber-950/50 dark:border-amber-800 dark:text-amber-300";
            case "NEVER_WORKED":
                return "bg-red-100 border-red-300 text-red-700 dark:bg-red-950/50 dark:border-red-800 dark:text-red-300";
            default:
                return "bg-slate-100 border-slate-300 text-slate-700 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300";
        }
    };

    const formatStateText = (state: string) => {
        switch (state) {
            case "PAIN_REDUCED":
                return "Pain Reduced";
            case "PAIN_SPIKE":
                return "Pain Spike";
            case "NEVER_WORKED":
                return "Never Worked";
            default:
                return state;
        }
    };

    const getFlagColor = (state: string): "green" | "amber" | "red" => {
        switch (state) {
            case "PAIN_REDUCED":
                return "green";
            case "PAIN_SPIKE":
                return "amber";
            case "NEVER_WORKED":
                return "red";
            default:
                return "green";
        }
    };

    // Auto-advance tabs
    useEffect(() => {
        if (isPaused) return;

        autoAdvanceRef.current = window.setInterval(() => {
            setDiaryIndex((prev) => (prev + 1) % exampleDiaries.length);
        }, 4000); // Change every 4 seconds

        return () => {
            if (autoAdvanceRef.current) {
                clearInterval(autoAdvanceRef.current);
            }
        };
    }, [diaryIndex, isPaused]);

    const handleTabInteraction = (index: number) => {
        setDiaryIndex(index);
        setIsPaused(true);

        // Resume after 1 second
        setTimeout(() => {
            setIsPaused(false);
        }, 1000);
    };

    return (
        <div className="w-full">
            <div
                className="flex flex-wrap gap-2 mb-4 w-full"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => {
                    setTimeout(() => setIsPaused(false), 1000);
                }}
            >
                <Tab
                    active={diaryIndex === 0}
                    onClick={() => handleTabInteraction(0)}
                    icon={<Flag color="green" className="w-5 h-5" />}
                    words="Pain Reduced"
                    color="green"
                />
                <Tab
                    active={diaryIndex === 1}
                    onClick={() => handleTabInteraction(1)}
                    icon={<Flag color="amber" className="w-5 h-5" />}
                    words="Pain Spike"
                    color="amber"
                />
                <Tab
                    active={diaryIndex === 2}
                    onClick={() => handleTabInteraction(2)}
                    icon={<Flag color="red" className="w-5 h-5" />}
                    words="Never Worked"
                    color="red"
                />
            </div>

            <div className="bg-white/70 dark:bg-slate-900/80 shadow-md p-6 border border-slate-200 dark:border-slate-700 rounded-lg">
                <div className="flex flex-col gap-y-2 mb-4">
                    <div className="flex justify-between items-start w-full">
                        <div className="flex flex-col gap-y-0">
                            <h2 className="text-slate-800 dark:text-slate-100 text-2xl">{currentDiary.patient_name}</h2>
                            <span className="text-slate-600 dark:text-slate-400 text-sm">Motown Medical Centre</span>
                        </div>
                        <div
                            className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1 rounded-sm sm:aspect-square md:aspect-auto font-medium text-sm border ${getStateStyles(
                                currentDiary.current_state
                            )}`}
                        >
                            <Flag color={getFlagColor(currentDiary.current_state)} className="w-6 md:w-8 h-6 md:h-8" />
                            <span className="hidden md:block">{formatStateText(currentDiary.current_state)}</span>
                        </div>
                    </div>
                    <hr className="border-slate-200 dark:border-slate-700" />
                    <div className="w-full min-h-[400px]">
                        <GenericChart entries={currentDiary.entries} formId="pain-form" chartType="line" />
                    </div>
                </div>
            </div>
        </div>
    );
}
